import {
  insertLibraryEntrySchema,
  insertLibraryPathSchema,
  patchLibraryEntrySchema,
  patchLibraryPathSchema,
} from "@/api/db/schema/library";
import { libraryService } from "@/api/services/library.service";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { authMiddlewareServer } from "../middleware/auth";

const requireEditor = (role: string) => {
  if (role !== "admin" && role !== "author") throw new Error("Unauthorized");
};

export const listLibraryPathsFn = createServerFn({ method: "GET" }).handler(
  async () => ({ paths: await libraryService.listPaths(true) }),
);

export const listPublishedLibraryEntriesFn = createServerFn({
  method: "GET",
}).handler(async () => ({ entries: await libraryService.listEntries(true) }));

export const listPublishedLibraryEntriesOptions = () =>
  queryOptions({
    queryKey: ["library-entries"],
    queryFn: () => listPublishedLibraryEntriesFn(),
  });

export const listLibraryPathsOptions = () =>
  queryOptions({
    queryKey: ["library-paths"],
    queryFn: () => listLibraryPathsFn(),
  });

export const getLibraryPathFn = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data }) => ({
    path: await libraryService.getPathBySlug(data.slug),
  }));

export const getLibraryPathOptions = (slug: string) =>
  queryOptions({
    queryKey: ["library-path", slug],
    queryFn: () => getLibraryPathFn({ data: { slug } }),
  });

export const getLibraryEntryFn = createServerFn({ method: "GET" })
  .inputValidator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data }) => ({
    entry: await libraryService.getEntryBySlug(data.slug),
  }));

export const getLibraryEntryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["library-entry", slug],
    queryFn: () => getLibraryEntryFn({ data: { slug } }),
  });

export const listLibraryAdminFn = createServerFn({ method: "GET" })
  .middleware([authMiddlewareServer])
  .handler(async ({ context }) => {
    requireEditor(context.auth.role);
    return {
      paths: await libraryService.listPaths(false),
      entries: await libraryService.listEntries(false),
      questions: await (
        await import("@/api/db/db.index")
      ).db.query.tags.findMany({
        where: (table, { eq }) => eq(table.moderationStatus, "published"),
        columns: { tagId: true, tagTitle: true, slug: true },
        orderBy: (table, { desc }) => [desc(table.lastActivityAt)],
      }),
    };
  });

export const createLibraryPathFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertLibraryPathSchema)
  .handler(async ({ context, data }) => {
    requireEditor(context.auth.role);
    return { path: await libraryService.createPath(data, context.auth.userId) };
  });

export const updateLibraryPathFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchLibraryPathSchema)
  .handler(async ({ context, data }) => {
    requireEditor(context.auth.role);
    const { pathId, ...path } = data;
    return { path: await libraryService.updatePath(pathId, path) };
  });

export const createLibraryEntryFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(insertLibraryEntrySchema)
  .handler(async ({ context, data }) => {
    requireEditor(context.auth.role);
    return {
      entry: await libraryService.createEntry(data, context.auth.userId),
    };
  });

export const updateLibraryEntryFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(patchLibraryEntrySchema)
  .handler(async ({ context, data }) => {
    requireEditor(context.auth.role);
    const { entryId, ...entry } = data;
    return { entry: await libraryService.updateEntry(entryId, entry) };
  });

const crossrefResponseSchema = z.object({
  message: z.object({
    DOI: z.string().optional(),
    URL: z.string().optional(),
    title: z.array(z.string()).optional(),
    author: z
      .array(
        z.object({
          given: z.string().optional(),
          family: z.string().optional(),
        }),
      )
      .optional(),
    "container-title": z.array(z.string()).optional(),
    published: z
      .object({ "date-parts": z.array(z.array(z.number())) })
      .optional(),
    type: z.string().optional(),
  }),
});

export const lookupDoiFn = createServerFn({ method: "POST" })
  .middleware([authMiddlewareServer])
  .inputValidator(z.object({ doi: z.string().trim().min(5).max(200) }))
  .handler(async ({ context, data }) => {
    requireEditor(context.auth.role);
    const doi = data.doi.replace(/^https?:\/\/(dx\.)?doi\.org\//i, "");
    const contact =
      process.env.CROSSREF_CONTACT_EMAIL || "noreply@mail.atalaythecoach.com";
    const response = await fetch(
      `https://api.crossref.org/works/${encodeURIComponent(doi)}?mailto=${encodeURIComponent(contact)}`,
      {
        headers: {
          "User-Agent": `AtalayTheCoach-Library/1.0 (mailto:${contact})`,
        },
        signal: AbortSignal.timeout(8000),
      },
    );
    if (!response.ok) throw new Error("DOI Crossref üzerinde bulunamadı");
    const parsed = crossrefResponseSchema.parse(await response.json()).message;
    const parts = parsed.published?.["date-parts"]?.[0] || [];
    const year = parts[0] || new Date().getFullYear();
    const month = String(parts[1] || 1).padStart(2, "0");
    const day = String(parts[2] || 1).padStart(2, "0");
    return {
      metadata: {
        doi: parsed.DOI || doi,
        sourceUrl: parsed.URL || `https://doi.org/${doi}`,
        originalTitle: parsed.title?.[0] || "",
        authors:
          parsed.author?.map((author) =>
            [author.given, author.family].filter(Boolean).join(" "),
          ) || [],
        journal: parsed["container-title"]?.[0] || null,
        publicationDate: `${year}-${month}-${day}`,
        crossrefType: parsed.type || null,
      },
    };
  });
